import type { ReactNode } from "react";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { Reservation } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon, StarRating, TripadvisorRatingIcon } from "@tenant/ui";
import clsx from "clsx";
import styles from "./TripSummaryCard.module.scss";
import { useTripSummaryCard } from "./useTripSummaryCard";

interface TripSummaryCardProps {
  className?: string;
  reservation: Reservation;
  hasCfar?: boolean;
  title?: ReactNode;
  cta?: ReactNode;
}

export const TripSummaryCard = ({
  reservation,
  className,
  hasCfar = false,
  title,
  cta,
}: TripSummaryCardProps) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "TripSummaryCard");
  const data = useTripSummaryCard(reservation);

  if (data == null) return null;

  return (
    <div className={clsx(block, className)}>
      {title && <div className={cn("title")}>{title}</div>}
      {data.image != null && (
        <div className={cn("image")}>
          <img src={data.image} alt={data.name} />
        </div>
      )}
      <div className={cn("details")}>
        <div className={cn("hotel-meta")}>
          <StarRating starRating={data.starRating} />
          <span className={cn("location")}>
            <Icon iconName={IconNameEnum.mapPin} />
            {data.location}
          </span>
          {data.tripAdvisorScore != null && (
            <span className={cn("tripadvisor")}>
              <Icon
                className={cn("tripadvisor-icon")}
                iconName={IconNameEnum.tripadvisor}
              />
              <TripadvisorRatingIcon scoreOutOfFive={data.tripAdvisorScore} />
              {data.tripAdvisorReviewCount != null && (
                <span className={cn("tripadvisor-count")}>
                  {data.tripAdvisorReviewCount}
                </span>
              )}
            </span>
          )}
        </div>
        <h2 className={cn("hotel-name")}>{data.name}</h2>
        <div className={cn("address")}>{data.address}</div>

        <div className={cn("dates")}>
          <div className={cn("date-group")}>
            <span className={cn("date-label")}>
              {t("core-hotels.postBooking.reservation.checkInLabel")}
            </span>
            <span className={cn("date-value")}>{data.checkInDate}</span>
          </div>
          <div className={cn("date-group")}>
            <span className={cn("date-label")}>
              {t("core-hotels.postBooking.reservation.checkOutLabel")}
            </span>
            <span className={cn("date-value")}>{data.checkOutDate}</span>
          </div>
        </div>

        <div className={cn("room-info")}>
          {(data.roomName !== "" || data.beds !== "") && (
            <div className={cn("room-group")}>
              <span className={cn("room-label")}>{data.roomName}</span>
              {data.beds !== "" && (
                <span className={cn("room-value")}>{data.beds}</span>
              )}
            </div>
          )}
          {data.guestSummary !== "" && (
            <div className={cn("room-group")}>
              <span className={cn("room-label")}>
                {t(
                  "core-hotels.postBooking.reservation.reservationDetails.numberOfGuestsTitle"
                )}
              </span>
              <span className={cn("room-value")}>{data.guestSummary}</span>
            </div>
          )}
        </div>
      </div>

      {data.isRefundable &&
        data.cancellationPrimaryText != null &&
        !hasCfar && (
          <div className={cn("refundable-notice")}>
            <Icon
              className={cn("refundable-icon")}
              iconName={IconNameEnum.disruptionProtection}
            />
            <span>{data.cancellationPrimaryText}</span>
          </div>
        )}
      {cta && <div className={cn("cta")}>{cta}</div>}
    </div>
  );
};
